import React, { useEffect, useState } from "react";
import { API_BASE } from "../../api/config";
import { modalBox, modalInput, modalLabel, modalPrimary } from "./FolgevertragModal";

const fmt = (v) => Number(v || 0).toLocaleString("de-DE", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €";
const fmtDate = (d) => (d ? String(d).slice(0, 10).split("-").reverse().join(".") : "–");

/**
 * Umsatz → Mietvertrag zuordnen (Welle 83).
 * Props: zeile (aus /zahlungen-auto/offen), onClose(), onDone(result)
 */
export default function ZuordnenDialog({ zeile, onClose, onDone }) {
  const vorschlag = zeile.vorschlag || {};
  // Welle 106.1: Gibt es einen Automatik-Vorschlag, gewinnt dessen Objekt — sonst steht
  // im Auswahlfeld ein anderes Objekt und der vorgeschlagene Vertrag ist gar nicht wählbar.
  const [objekt, setObjekt] = useState((vorschlag.mietvertrag_nr || "").slice(0, 3) || zeile.objekt || "");
  const [objekte, setObjekte] = useState([]);
  const [vertraege, setVertraege] = useState([]);
  const [mv, setMv] = useState(vorschlag.mietvertrag_nr || (vorschlag.kandidaten?.[0]?.mietvertrag_nr) || "");
  const [merken, setMerken] = useState(zeile.unkategorisiert ? "nur_diese" : "iban");
  const [monat, setMonat] = useState(zeile.monat || "");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API_BASE}/mietverwaltung/api/vertraege`);
        const d = await res.json();
        if (d.status === "ok") {
          setObjekte(d.objekt_optionen || []);
          setVertraege(d.vertraege || []);
        }
      } catch (e) { setFehler(String(e.message || e)); }
    })();
  }, []);

  // Alle Verträge des Objekts (auch beendete) über vertrag-detail wäre teuer — Liste zeigt letzte Verträge je Einheit,
  // Kandidaten aus dem Vorschlag werden zusätzlich angeboten.
  const kandidaten = vorschlag.kandidaten || [];
  const optionen = [];
  const gesehen = new Set();
  for (const k of kandidaten) {
    if (!gesehen.has(k.mietvertrag_nr)) { gesehen.add(k.mietvertrag_nr); optionen.push({ nr: k.mietvertrag_nr, name: k.name, status: "Kandidat" }); }
  }
  for (const v of vertraege.filter((x) => !objekt || x.objekt === objekt)) {
    if (!gesehen.has(v.mietvertrag_nr)) { gesehen.add(v.mietvertrag_nr); optionen.push({ nr: v.mietvertrag_nr, name: v.name, status: v.status }); }
  }
  // Der vorgeschlagene Vertrag muss immer wählbar sein, auch wenn er nicht in der Liste steht
  if (vorschlag.mietvertrag_nr && !gesehen.has(vorschlag.mietvertrag_nr)) {
    const v = vertraege.find((x) => x.mietvertrag_nr === vorschlag.mietvertrag_nr);
    optionen.unshift({ nr: vorschlag.mietvertrag_nr, name: v?.name || vorschlag.name || "", status: "Vorschlag" });
  }

  const senden = async (ignorieren) => {
    setBusy(true); setFehler("");
    try {
      const res = await fetch(`${API_BASE}/mietverwaltung/api/zahlungen-auto/${ignorieren ? "ignorieren" : "zuordnen"}`, {
        method: "POST", headers: { "Content-Type": "application/json" },
        body: JSON.stringify(ignorieren ? { daten_id: zeile.daten_id }
          : { daten_id: zeile.daten_id, mietvertrag_nr: mv, merken, monat: monat || null }),
      });
      const d = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(d.detail || "Fehlgeschlagen");
      onDone?.(d);
    } catch (e) { setFehler(String(e.message || e)); }
    setBusy(false);
  };

  return (
    <div className="me-drawer-overlay" style={{ display: "flex", alignItems: "center", justifyContent: "center" }} onClick={onClose}>
      <div style={{ ...modalBox, width: "min(600px, 94vw)" }} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ margin: 0 }}>Umsatz zuordnen</h3>
        <div style={{ fontSize: 13, color: "#cbd5e1", marginTop: 8, lineHeight: 1.5 }}>
          <b>{fmtDate(zeile.buchungstag)}</b> · <b>{fmt(zeile.betrag)}</b> · {zeile.beguenstigter}<br />
          <span style={{ color: "#94a3b8" }}>{zeile.verwendungszweck}</span><br />
          <span style={{ color: "#94a3b8" }}>Konto/Objekt {zeile.objekt || "–"} · IBAN {zeile.iban_kurz || "–"} · Kategorie {zeile.kategorie || "–"}{zeile.subkat ? ` / ${zeile.subkat}` : ""}</span>
          {vorschlag.grund && <div style={{ marginTop: 4, color: "#fcd34d" }}>Automatik: {vorschlag.grund}{vorschlag.mietvertrag_nr ? ` → ${vorschlag.mietvertrag_nr}` : ""}</div>}
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: "10px 14px", marginTop: 12 }}>
          <label style={modalLabel}>Objekt
            <select style={modalInput} value={objekt} onChange={(e) => { setObjekt(e.target.value); setMv(""); }}>
              <option value="">alle</option>
              {objekte.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
          </label>
          <label style={modalLabel}>Mietvertrag *
            <select style={modalInput} value={mv} onChange={(e) => setMv(e.target.value)}>
              <option value="">– wählen –</option>
              {optionen.map((o) => <option key={o.nr} value={o.nr}>{o.nr} · {o.name || "–"} ({o.status})</option>)}
            </select>
          </label>
          <label style={modalLabel}>Monat (Soll-Zeile)
            <input type="month" style={modalInput} value={monat} onChange={(e) => setMonat(e.target.value)} />
          </label>
          <div style={modalLabel}>Merken für künftige Umsätze
            <div style={{ display: "flex", gap: 14, alignItems: "center", height: 34 }}>
              {[["iban", "IBAN"], ["name", "Zahler-Name"], ["nur_diese", "nur diesen Umsatz"]].map(([k, l]) => (
                <label key={k} style={{ display: "inline-flex", gap: 5, alignItems: "center", cursor: "pointer" }}>
                  <input type="radio" name="merken" checked={merken === k} onChange={() => setMerken(k)} /> {l}
                </label>
              ))}
            </div>
          </div>
        </div>
        {fehler && <p style={{ color: "#fca5a5", fontSize: 13 }}>{fehler}</p>}
        <div style={{ display: "flex", gap: 10, justifyContent: "space-between", marginTop: 16 }}>
          <button className="me-btn me-btn-outline" disabled={busy} onClick={() => senden(true)} title="Kein Mietumsatz — dauerhaft aus der Liste nehmen">Ignorieren</button>
          <div style={{ display: "flex", gap: 10 }}>
            <button className="me-btn me-btn-outline" onClick={onClose}>Abbrechen</button>
            <button className="me-btn" style={modalPrimary} disabled={busy || !mv} onClick={() => senden(false)}>
              {busy ? "Speichert…" : "Zuordnen"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
